import { useState, useEffect } from "react";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Increment this version whenever you make widget updates that require merchants to resync
export const CURRENT_WIDGET_VERSION = 2;

interface WidgetUpdateBannerProps {
  merchantId: string;
  merchantWidgetVersion: number | null;
  onResyncComplete?: () => void;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function WidgetUpdateBanner({ 
  merchantId, 
  merchantWidgetVersion,
  onResyncComplete 
}: WidgetUpdateBannerProps) {
  const { toast } = useToast();
  const [isResyncing, setIsResyncing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Check if widget is outdated
  const isOutdated = (merchantWidgetVersion ?? 1) < CURRENT_WIDGET_VERSION;

  // Don't show if up to date or dismissed
  if (!isOutdated || dismissed) {
    return null;
  }

  const handleResync = async () => {
    setIsResyncing(true);
    try {
      const { error } = await supabase.functions.invoke('install-widget', {
        body: { 
          merchantId,
          action: 'install',
          position: 'image-top-left'
        },
      });

      if (error) throw error;

      // Update merchant's widget version in database
      await supabase
        .from('merchants')
        .update({ 
          widget_version: CURRENT_WIDGET_VERSION,
          widget_synced_at: new Date().toISOString()
        })
        .eq('id', merchantId);

      toast({
        title: "Widget Updated!",
        description: "Your store widget has been synced to the latest version.",
      });

      onResyncComplete?.();
      setDismissed(true);
    } catch (error: any) {
      console.error("Error resyncing widget:", error);
      toast({
        title: "Sync Failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsResyncing(false);
    }
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200 text-sm">
              Widget Update Available
            </p>
            <p className="text-amber-700 dark:text-amber-300 text-xs">
              A new version of the split payment widget is available. Resync to get the latest features.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            onClick={handleResync}
            disabled={isResyncing}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isResyncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-1" />
                Resync Now
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            className="text-amber-700 hover:text-amber-800"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Hook to check if widget needs update (for notification badge)
export function useWidgetUpdateCheck(merchantWidgetVersion: number | null) {
  const isOutdated = (merchantWidgetVersion ?? 1) < CURRENT_WIDGET_VERSION;
  return { needsUpdate: isOutdated, currentVersion: CURRENT_WIDGET_VERSION };
}
